import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { getTokens } from "../../../services/data";
import {
  AuditoriaUsuario,
  AuditoriaUsuariosParams,
  listarAuditoriaUsuarios,
  resumirAuditoriaUsuarios,
} from "../../../services/auditoriaUsuarios";

export const filtrosUsuarios = ["id_usuarios", "nome", "email", "projeto", "permissao", "tipo"];
const ordenacaoUsuarios = ["id_usuarios", "nome", "email", "ativo", "administrador", "tipo"];

export function readUsuariosParams(search: string): AuditoriaUsuariosParams {
  const params = new URLSearchParams(search);
  const rawLimit = Number(params.get("limit") || 10);
  const limit = [5, 10, 25, 50, 100].includes(rawLimit) ? rawLimit : 10;
  const rawOffset = Number(params.get("offset") || 0);
  const offset =
    Number.isInteger(rawOffset) && rawOffset >= 0 && rawOffset <= 2147483647
      ? Math.floor(rawOffset / limit) * limit
      : 0;
  const coluna = params.get("coluna") || "";
  const filtro = (params.get("filtro") || "").trim();
  const status = params.get("status");
  const perfil = params.get("perfil");
  const orderBy = params.get("orderBy") || "nome";
  return {
    limit,
    offset,
    coluna: filtrosUsuarios.includes(coluna) && filtro ? coluna : "",
    filtro: filtrosUsuarios.includes(coluna) && filtro ? filtro : "",
    status: status === "ativos" || status === "inativos" ? status : "todos",
    perfil: perfil === "administradores" || perfil === "demais" ? perfil : "todos",
    orderBy: ordenacaoUsuarios.includes(orderBy) ? orderBy : "nome",
    direction: params.get("direction") === "desc" ? "desc" : "asc",
  };
}

export default function useAuditoriaUsuarios() {
  const location = useLocation();
  const navigate = useNavigate();
  const client = useQueryClient();
  const params = readUsuariosParams(location.search);
  const [coluna, setColuna] = useState(params.coluna);
  const [filtro, setFiltro] = useState(params.filtro);
  const tokens = getTokens();
  const scope = [tokens["x-tn-token"], tokens["x-auth-token"]];
  useEffect(() => {
    setColuna(params.coluna);
    setFiltro(params.filtro);
  }, [params.coluna, params.filtro]);
  const query = useQuery({
    queryKey: ["auditoria-usuarios", ...scope, params],
    queryFn: ({ signal }) => listarAuditoriaUsuarios(params, signal),
    staleTime: 60000,
  });
  const resumo = useQuery({
    queryKey: ["auditoria-usuarios-resumo", ...scope, params.coluna, params.filtro, params.status, params.perfil],
    queryFn: ({ signal }) => resumirAuditoriaUsuarios(params, signal),
    staleTime: 60000,
  });
  function update(next: Partial<AuditoriaUsuariosParams>) {
    const search = new URLSearchParams(location.search);
    Object.entries({ ...params, ...next }).forEach(([key, value]) => {
      if (value === "" || value === "todos") search.delete(key);
      else search.set(key, String(value));
    });
    navigate({ pathname: location.pathname, search: search.toString() });
  }
  const valid = Boolean(
    coluna &&
      filtro.trim() &&
      filtro.trim().length <= 500 &&
      (coluna !== "id_usuarios" || (/^[1-9]\d*$/.test(filtro.trim()) && Number(filtro) <= 2147483647)),
  );
  async function exportar(): Promise<AuditoriaUsuario[]> {
    const rows: AuditoriaUsuario[] = [];
    let offset = 0;
    let hasMore = true;
    const assertSession = () => {
      const current = getTokens();
      if (current["x-tn-token"] !== tokens["x-tn-token"] || current["x-auth-token"] !== tokens["x-auth-token"])
        throw new Error("A sessão mudou durante a exportação.");
    };
    while (hasMore) {
      assertSession();
      const page = await listarAuditoriaUsuarios({ ...params, limit: 1000, offset });
      assertSession();
      rows.push(...page.data);
      offset += page.data.length;
      hasMore = page.hasMore && page.data.length > 0;
    }
    return rows;
  }
  return {
    query,
    resumo,
    params,
    scope,
    coluna,
    setColuna,
    filtro,
    setFiltro,
    valid,
    apply: () => {
      if (valid) update({ coluna, filtro: filtro.trim(), offset: 0 });
    },
    clear: () => {
      setColuna("");
      setFiltro("");
      update({ coluna: "", filtro: "", status: "todos", perfil: "todos", offset: 0 });
    },
    update,
    sort: (field: string) =>
      update({
        orderBy: field,
        direction: params.orderBy === field && params.direction === "asc" ? "desc" : "asc",
        offset: 0,
      }),
    refresh: () =>
      Promise.all([
        client.invalidateQueries({ queryKey: ["auditoria-usuarios", ...scope] }),
        client.invalidateQueries({ queryKey: ["auditoria-usuarios-resumo", ...scope] }),
        client.invalidateQueries({ queryKey: ["auditoria-usuario-detalhe", ...scope] }),
      ]),
    exportar,
  };
}
